// Copyright (c) 2020-2021 Visual Intelligence LP. All rights reserved.
//
import * as React from 'react';
import { observer } from 'mobx-react';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { withStyles } from "tss-react/mui";
import { Dialog, DialogContent, DialogActions } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Link } from 'react-router-dom';

import container from '@core/di';
import EntitiesTemplatesStore, { ENTITIES } from '@core/stores/entities-templates';
import OrdersStore from '@shared/stores/orders';
import DeliverablesStore from '@shared/stores/deliverables';
import Deliverable, { DeliverableDTO } from '@shared/models/deliverable';
import { AssetDTO } from '@shared/models/asset';
import ROUTES from '@shared/constants/routes';
import { DELIVERABLE_FIELDS, DELIVERABLE_STATUS_LABELS } from '@shared/constants/deliverable';
import { generateId } from '@shared/utils/common';
import { resetForm } from '@shared/utils/form';
import Form, { ManageMode, FormProps, Data } from '@shared/components/Form';
import Select, { Option, normalize as selectNormalize, getFieldOption as getSelectFieldOption, SerializerType } from '@shared/components/Select';
import DatePicker from '@shared/components/DatePicker';
import Checkbox from '@shared/components/Checkbox';
import Field from '@shared/components/Form/components/Field';
import Button from '@shared/components/Button';
import DeliverableForm from '@modules/Private/submodules/Orders/components/Deliverable/forms/deliverable';
import { showNotification, NotificationType } from '@shared/components/Notification';
import { Column } from '@shared/components/Table/Table.types';
import Datum from '@shared/models/deliverable/datum';
import Projection from '@shared/models/deliverable/projection';
import { DELIVERABLE_DATUM_FIELDS } from '@shared/constants/deliverable-datum';
import { DELIVERABLE_PROJECTION_FIELDS } from '@shared/constants/deliverable-projection';

export interface DeliverableManagementProps {
  className?: string;
  manageMode: ManageMode;
  orderManageMode: ManageMode;
  assetId: AssetDTO['id'];
  deliverableId: DeliverableDTO['id'];
  maxDueDate: string;
  defaultDueDate: string;
  defaultProjection?: { id: DeliverableDTO['projectionId'], name: DeliverableDTO['projectionName'] };
  defaultDatum?: { id: DeliverableDTO['datumId'], name: DeliverableDTO['datumName'] };
  formComponentProps?: Partial<FormProps>;
  doesDeliverableExistInOrder?: boolean;
  onSubmit: (manageMode: ManageMode, assetId: AssetDTO['id'], deliverableId: DeliverableDTO['id'], data: Partial<DeliverableDTO>) => any;
}

@observer
class DeliverableManagement extends React.Component<DeliverableManagementProps> {
  @observable private form;
  private ordersStore = container.get<OrdersStore>(OrdersStore.diToken);
  private deliverablesStore = container.get<DeliverablesStore>(DeliverablesStore.diToken);
  private entitiesTemplateStore = container.get<EntitiesTemplatesStore>(EntitiesTemplatesStore.diToken);
  @observable storedData;
  @observable loading = false;
  @observable deliverable: Deliverable | undefined;

  constructor(props: DeliverableManagementProps) {
    super(props);

    this.form = new DeliverableForm();
    this.deliverable = undefined;
    this.deliverablesStore.getDeliverableTypes();
    this.storedData = undefined;

    const { manageMode } = props;

    if (manageMode === ManageMode.edit || this.hasTemplate) {
      this.updateFormData();
    } else {
      this.setDefaultValues();
    }

    makeObservable(this);
  }

  private get hasTemplate() {
    return this.entitiesTemplateStore.hasTemplate(ENTITIES.deliverable);
  }

  private get availableDeliverableTypes() {
    const { manageMode, assetId } = this.props;
    const availableTypes = this.ordersStore.getAvailableDeliverableTypes(
      assetId,
      manageMode === ManageMode.create ? undefined : this.deliverable?.deliverableType
    );

    return selectNormalize(availableTypes);
  }

  private get isLinkedWithJobRequest() {
    const { manageMode } = this.props;

    if (manageMode === ManageMode.create || !this.deliverable) {
      return false;
    }

    const { assetId } = this.props;

    return this.ordersStore.isDeliverableLinkedWithJobRequest(assetId, this.deliverable.deliverableType);
  }

  private get canManage() {
    const { manageMode, orderManageMode, doesDeliverableExistInOrder } = this.props;

    if (manageMode === ManageMode.create || orderManageMode === ManageMode.create || !this.deliverable || !doesDeliverableExistInOrder) {
      return true;
    }

    const { dimensionId, status } = this.deliverable;

    return this.ordersStore.canEditDeliverable(dimensionId, status);
  }

  // private get selectDatumsTableConfig(): TableSelectProps['baseSelectorComponentProps'] {
  //   const { deliverableDatums, loading, deliverableDatumsPageInfo, getDeliverableDatums } = this.deliverablesStore;

  //   return {
  //     config: {
  //       pageInfo: deliverableDatumsPageInfo,
  //       defaultOrderField: DELIVERABLE_FIELDS.name,
  //       getData: getDeliverableDatums
  //     },
  //     selectionType: SelectionType.single,
  //     data: deliverableDatums,
  //     loading: loading.datums,
  //     columns: this.datumColumns,
  //   };
  // }

  // private get selectProjectionsTableConfig(): TableSelectProps['baseSelectorComponentProps'] {
  //   const { deliverableProjections, loading, deliverableProjectionsPageInfo, getDeliverableProjections } = this.deliverablesStore;

  //   return {
  //     config: {
  //       pageInfo: deliverableProjectionsPageInfo,
  //       defaultOrderField: DELIVERABLE_FIELDS.name,
  //       getData: getDeliverableProjections
  //     },
  //     selectionType: SelectionType.single,
  //     data: deliverableProjections,
  //     loading: loading.projections,
  //     columns: this.projectionColumns,
  //   };
  // }

  // private get datumColumns(): Array<Column<Datum>> {
  //   return [
  //     {
  //       name: 'Name',
  //       field: DELIVERABLE_DATUM_FIELDS.name,
  //       width: 400,
  //     },
  //   ];
  // }

  // private get projectionColumns(): Array<Column<Projection>> {
  //   return [
  //     {
  //       name: 'Name',
  //       field: DELIVERABLE_PROJECTION_FIELDS.name,
  //       width: 400,
  //     },
  //   ];
  // }

  // Configure the fields for the 'Add deliverable' form.
  private get fields() {
    const { form } = this;
    const { maxDueDate } = this.props;
    const { loading } = this.deliverablesStore;
    const areDisabledAllFields = !this.canManage;
    const classes = withStyles.getClasses(this.props);

    return [
      {
        label: 'Deliverable',
        component: (
          <Select
            // fullWidth
            classes={{ root: classes.deliverableSelect}}
            disabled={this.isLinkedWithJobRequest && !areDisabledAllFields}
            serializerType={SerializerType.number}
            options={this.availableDeliverableTypes}
            field={form.$(DELIVERABLE_FIELDS.deliverableType)}
            placeholder="Select deliverable"
            loading={loading.types}
            testAttributeArgs={{
              module: 'Deliverable-management',
              elementDescription: 'deliverable-type',
            }}
          />
        ),
      },
      {
        label: 'Internal deliverable',
        component: <Checkbox field={form.$(DELIVERABLE_FIELDS.isInternal)} />
      },
      // {
      //   label: 'Projection',
      //   component: (
      //     <TableSelect 
      //       searchPlaceholder="Search by code, name"
      //       maxWidth="sm"
      //       moduleName="Deliverable-management"
      //       placeholder="Select a projection"
      //       labelProperty={DELIVERABLE_FIELDS.name}
      //       field={form.$(DELIVERABLE_FIELDS.projectionId)}
      //       classes={{ root: classes.datumProjectionField }}
      //       baseSelectorComponentProps={this.selectProjectionsTableConfig}
      //     />
      //   )
      // },
      // {
      //   label: 'Datum',
      //   component: (
      //     <TableSelect 
      //       searchPlaceholder="Search by code, name"
      //       maxWidth="sm"
      //       moduleName="Deliverable-management"
      //       placeholder="Select a vertical datum" 
      //       labelProperty={DELIVERABLE_FIELDS.name}
      //       field={form.$(DELIVERABLE_FIELDS.datumId)}
      //       classes={{ root: classes.datumProjectionField }}
      //       baseSelectorComponentProps={this.selectDatumsTableConfig}
      //     />
      //   ) 
      // },
      {
        label: 'Due',
        component: (
          <DatePicker
            endOfUnit="day"
            field={form.$(DELIVERABLE_FIELDS.deadline)}
            placeholder="Due date"
            maxDate={maxDueDate}
            testAttributeArgs={{
              module: 'Deliverable-management',
              elementDescription: 'due-date'
            }}
          />
        )
      },
    ];
  }

  private get formConfig() {
    const { formComponentProps } = this.props;
    const defaultCloseLocation = { pathname: `${ROUTES.private.orders}/${ManageMode.create}` };
    const closeLocation = formComponentProps?.closeLocation || defaultCloseLocation;

    const config = {
      [ManageMode.create]: {
        closeLocation,
        heading: 'Add deliverable',
        successMessage: 'Deliverable has been successfully created',
      },
      [ManageMode.edit]: {
        closeLocation,
        heading: 'Edit deliverable',
        successMessage: 'Deliverable has been successfully edited',
      },
    };

    return config[this.props.manageMode];
  }

  @action setDefaultValues = () => {
    const { defaultProjection, defaultDatum, defaultDueDate } = this.props;
    const notSelectedProjectionId = 1;

    // if (defaultProjection && (defaultProjection?.id !== notSelectedProjectionId)) { // TODO remove after backend migration compete
    //   setFieldLabel(this.form.$(DELIVERABLE_FIELDS.projectionId), defaultProjection?.name || '');
    //   this.form.$(DELIVERABLE_FIELDS.projectionId).set('value', defaultProjection?.id);
    // }

    // if (defaultDatum) {
    //   setFieldLabel(this.form.$(DELIVERABLE_FIELDS.datumId), defaultDatum?.name || '');
    //   this.form.$(DELIVERABLE_FIELDS.datumId).set('value', defaultDatum?.id);
    // }

    this.form.$(DELIVERABLE_FIELDS.deadline).set('value', defaultDueDate);
    this.updateStoredData();
    this.form.validate();
  }

  @action processDeliverableData = (recentlyCreatedDeliverable?: Omit<DeliverableDTO, 'id'>) => {
    if (!this.deliverable) {
      return;
    }

    const { defaultDueDate, manageMode, assetId } = this.props;
    const isCreating = manageMode === ManageMode.create;

    const { deadline, ...otherDeliverableData } = this.deliverable.asJson;

    this.form.update({
      ...otherDeliverableData,
      [DELIVERABLE_FIELDS.deadline]: isCreating ? defaultDueDate : deadline
    });

    if (isCreating) {
      const assetDeliverables = this.ordersStore.deliverables[assetId];
      const deliverableTypeExistsInAsset = (
        recentlyCreatedDeliverable?.deliverableType == this.deliverable?.deliverableType ||
        assetDeliverables.some((assetDeliverable) => {
          return assetDeliverable.deliverableType == this.deliverable?.deliverableType;
        })
      );

      if (deliverableTypeExistsInAsset) {
        this.form.$(DELIVERABLE_FIELDS.deliverableType).reset();
      }
    }

    // setFieldLabel(this.form.$(DELIVERABLE_FIELDS.projectionId), this.deliverable.projectionName);

    // if (this.deliverable.datumId) {
    //   setFieldLabel(this.form.$(DELIVERABLE_FIELDS.datumId), this.deliverable.datumName);
    // }

    this.updateStoredData();
    this.form.validate();
  }

  private getLocalDeliverable = (deliverableId: DeliverableDTO['id']) => {
    const { assetId } = this.props;

    return this.ordersStore.deliverables[assetId].find(({ id }) => id == deliverableId);
  }

  private getDeliverable = (deliverableId: DeliverableDTO['id']): Deliverable | Promise<Deliverable> | undefined => {
    const { manageMode, doesDeliverableExistInOrder } = this.props;

    if (doesDeliverableExistInOrder) {
      return this.deliverablesStore.get(deliverableId);
    }

    const localDeliverable = this.getLocalDeliverable(deliverableId);

    if (manageMode === ManageMode.create) {
      return localDeliverable || this.deliverablesStore.get(deliverableId);
    }

    return localDeliverable;
  }

  private updateFormData = async () => {
    runInAction(() => {
      this.loading = true;
    });

    const { manageMode, deliverableId } = this.props;
    const isCreating = manageMode === ManageMode.create;

    try {
      const templateId = this.entitiesTemplateStore.getTemplate(ENTITIES.deliverable);
      const finalDeliverableId = isCreating ? templateId : deliverableId;

      const deliverable = await this.getDeliverable(finalDeliverableId);

      runInAction(() => {
        this.deliverable = deliverable;
        this.processDeliverableData();
        this.loading = false;
      });
    } catch {
      runInAction(() => {
        this.loading = false;
      });

      if (isCreating) {
        this.setDefaultValues();

        return;
      }

      showNotification('Failed to load deliverable data', NotificationType.error);
    }
  }

  @action updateStoredData = (data: Data = this.form.values()) => {
    this.storedData = data;
  }

  @action resetForm = () => {
    resetForm(this.form);
    this.updateStoredData();
  }

  private shallNextLocationTerminateFormInstance = (currLocation: Location, nextLocation: Location) => {
    return currLocation.search != nextLocation.search;
  }

  // Even handler for submitting from the 'Add deliverable' form
  private handleSubmit = async (data: Omit<DeliverableDTO, 'id'>, withCreateAnother: boolean) => {
    const { assetId, deliverableId, manageMode, onSubmit } = this.props;

    const id = deliverableId || generateId();
    const createdAt = new Date().getTime();

    // Single selection here.
    const deliverableTypeOption = getSelectFieldOption(this.form.$(DELIVERABLE_FIELDS.deliverableType)) as Option;

    try {
      // bubble up to parent component <DeliverablesTable.
      await onSubmit(
        manageMode,
        assetId,
        id,
        {
          ...data,
          createdAt,
          // datumName: getFieldLabel(this.form.$(DELIVERABLE_FIELDS.datumId)),
          // projectionName: getFieldLabel(this.form.$(DELIVERABLE_FIELDS.projectionId)),
          deliverableTypeName: deliverableTypeOption?.label,
        }
      );

      if (manageMode === ManageMode.create) {
        if (withCreateAnother && this.hasTemplate && this.deliverable) {
          this.processDeliverableData(data);
        } else {
          this.resetForm();
          this.setDefaultValues();
        }
      }
    } 
    catch (err) {
      console.log("****** Exception in DeliMan handleSubmit:", err);
      throw err;
    }
  }

  private handleSetTemplateClick = () => {
    this.entitiesTemplateStore.setTemplate(ENTITIES.deliverable, this.props.deliverableId);
  }

  render() {
    const { manageMode } = this.props;
    const classes = withStyles.getClasses(this.props);

    // Show completion dialog for 'create'.
    if (manageMode === ManageMode.create && this.deliverablesStore.deliverableTypes.length && !this.availableDeliverableTypes.length) {
      return (
        <Dialog open classes={{ paper: classes.root }}>
          <DialogContent>
            <h6 className={classes.pleaceholderHeading}>
              All deliverables are added to the asset
            </h6>
          </DialogContent>
          <DialogActions>
            <Button
              text="Close"
              component={Link}
              componentProps={{ to: this.formConfig.closeLocation }}
              size="small"
            />
          </DialogActions>
        </Dialog>
      );
    }

    const { formComponentProps } = this.props;

    // Show 'Add deliverable' form.
    return (
      <Dialog
        open
        classes={{ paper: classes.root }}
      >
        <Form
          closeOnSubmit
          withTemplate
          viewOnlyMessage={this.deliverable?.status
            ? `Deliverable is in ${DELIVERABLE_STATUS_LABELS[this.deliverable.status].toLowerCase()} status`
            : ''
          }
          loadingData={this.loading}
          closeComponent={<CloseIcon />}
          heading={this.formConfig.heading}
          successMessage={this.formConfig.successMessage}
          form={this.form}
          manageMode={manageMode}
          moduleName="Deliverable-management"
          closeLocation={this.formConfig.closeLocation}
          classes={{ 
            root: classes.root,
            controls: classes.controls
          }}
          storedData={this.storedData}
          updateStoredData={this.updateStoredData}
          shallNextLocationTerminateFormInstance={this.shallNextLocationTerminateFormInstance}
          onSubmit={this.handleSubmit}
          onSetTemplateClick={this.handleSetTemplateClick}
          {...formComponentProps}
          viewOnly={!this.canManage || formComponentProps?.viewOnly}
        >
          {this.fields.map((field) => <Field {...field} key={field.label} classes={{ label: classes.fieldLabel }} />)}
        </Form>
      </Dialog>
    );
  }
}

const DeliverableManagementStyled = withStyles(
  DeliverableManagement, 
  (theme, props) => ({
    "root": {
      width: 600,
      maxWidth: 'none',
      margin: 0,
      border: 'none',
    },
    "pleaceholderHeading": {
      fontSize: 19,
      marginBottom: theme.spacing(6)
    },
    "controls": {
      justifyContent: 'flex-start',
      paddingLeft: 212
    },
    "datumProjectionField": {
      width: 240
    },
    "deliverableSelect": {
      width: 240,
    },
    "fieldLabel": {
      flexWrap: 'wrap',
      width: '30%'
    }
  })
);

export default DeliverableManagementStyled;